import { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    getPagesProblemsCount,
    getPagesProblemsList,
    type PageProblemGroup,
} from '@/services/api/connections';

const POLL_INTERVAL_MS = 60_000;
const MAX_DISPLAY_COUNT = 9;

export default function NotificationBell() {
    const [count, setCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [groups, setGroups] = useState<PageProblemGroup[]>([]);
    const [loadingItems, setLoadingItems] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // Guarda contra race condition: polling (60s) e o listener de visibilitychange
    // podem disparar fetchCount concorrentemente; só aplicamos o resultado da
    // chamada mais recente.
    const requestIdRef = useRef(0);

    useEffect(() => {
        let isMounted = true;

        const fetchCount = async () => {
            const requestId = ++requestIdRef.current;
            try {
                const data = await getPagesProblemsCount();
                if (isMounted && requestId === requestIdRef.current) setCount(data.count ?? 0);
            } catch (error) {
                // Falha silenciosa: notificação é informativa, não pode quebrar o layout.
                console.debug('Falha ao buscar contagem de páginas com problema:', error);
                if (isMounted && requestId === requestIdRef.current) setCount(0);
            }
        };

        fetchCount();
        intervalRef.current = setInterval(fetchCount, POLL_INTERVAL_MS);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') fetchCount();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            isMounted = false;
            if (intervalRef.current) clearInterval(intervalRef.current);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen) return;

        // Busca a lista completa só quando o painel abre — o polling acima
        // só mantém o número do badge atualizado, mais leve.
        setLoadingItems(true);
        getPagesProblemsList()
            .then((data) => setGroups(data.groups ?? []))
            .catch((error) => {
                console.debug('Falha ao buscar lista de páginas com problema:', error);
                setGroups([]);
            })
            .finally(() => setLoadingItems(false));
    };

    const toggleExpanded = (index: number) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    const hasProblems = count > 0;
    const displayCount = count > MAX_DISPLAY_COUNT ? `${MAX_DISPLAY_COUNT}+` : String(count);
    const ariaLabel = hasProblems
        ? `${count} página${count > 1 ? 's' : ''} com problema de conexão`
        : 'Nenhuma página com problema de conexão';

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-label={ariaLabel}
                    className="relative flex items-center justify-center h-10 w-10 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                    <Bell aria-hidden="true" className={`h-5 w-5 ${hasProblems ? 'text-gray-600' : 'text-gray-400'}`} />
                    {hasProblems && (
                        <span
                            aria-hidden="true"
                            className="absolute top-0.5 right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold leading-none"
                        >
                            {displayCount}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
                <div className="px-4 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900">Notificações</h3>
                </div>
                <div className="max-h-96 overflow-y-auto">
                    {loadingItems ? (
                        <p className="px-4 py-6 text-sm text-gray-500 text-center">Carregando...</p>
                    ) : groups.length === 0 ? (
                        <p className="px-4 py-6 text-sm text-gray-500 text-center">
                            Nenhum problema no momento — tudo certo com suas conexões.
                        </p>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {groups.map((group, index) => {
                                const isExpanded = expandedGroups.has(index);
                                const previewPages = group.pages.slice(0, 3);
                                const remaining = group.pages_count - previewPages.length;
                                return (
                                    <li key={`${group.meta_user_id}-${group.token_invalid_at}`} className="px-4 py-3">
                                        <p className="text-sm font-medium text-gray-900">
                                            {group.meta_user_name} desconectou o app — {group.pages_count} página
                                            {group.pages_count > 1 ? 's' : ''} afetada{group.pages_count > 1 ? 's' : ''}
                                        </p>
                                        <p className="text-sm text-gray-600 mt-0.5">{group.reason}</p>
                                        {group.token_invalid_at && (
                                            <p className="text-xs text-gray-400 mt-1">
                                                {formatDistanceToNow(new Date(group.token_invalid_at), {
                                                    addSuffix: true,
                                                    locale: ptBR,
                                                })}
                                            </p>
                                        )}
                                        <ul className="mt-2 space-y-0.5">
                                            {(isExpanded ? group.pages : previewPages).map((page) => (
                                                <li key={page.page_id} className="text-xs text-gray-500">
                                                    · {page.page_name || page.page_id}
                                                </li>
                                            ))}
                                        </ul>
                                        {remaining > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => toggleExpanded(index)}
                                                className="text-xs text-blue-600 hover:underline mt-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                                            >
                                                {isExpanded ? 'ver menos' : `e mais ${remaining} página${remaining > 1 ? 's' : ''}`}
                                            </button>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
